import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronRight } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { usePortalAccess } from '../hooks/usePortalAccess';
import { useCompanyId } from '../hooks/useCompanyId';
import { getAccountById } from '../services/firestoreService';
import PortalBackground from '../components/PortalBackground';
import PortalKickoffPriorities from '../components/portal/PortalKickoffPriorities';
import BBALoader from '../components/BBALoader';

const PortalKickoff = () => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const { companyId, loading: companyLoading } = useCompanyId();
  const { isPendingStart } = usePortalAccess();

  const [account, setAccount] = useState(null);
  const [accountLoading, setAccountLoading] = useState(true);

  // Load the account (priorities live on it)
  useEffect(() => {
    const load = async () => {
      if (!companyId) {
        setAccount(null);
        setAccountLoading(false);
        return;
      }
      setAccountLoading(true);
      try {
        const accountData = await getAccountById(companyId);
        setAccount(accountData || null);
      } catch (err) {
        console.error('Error loading kickoff data:', err);
        setAccount(null);
      } finally {
        setAccountLoading(false);
      }
    };
    load();
  }, [companyId]);

  const loading = companyLoading || accountLoading;
  const textMuted = darkMode ? 'text-gray-400' : 'text-gray-500';
  const textMain = darkMode ? 'text-gray-100' : 'text-gray-900';

  if (loading) {
    return (
      <div className={`p-6 ${textMain}`}>
        <PortalBackground />
        <div className="relative max-w-5xl mx-auto flex items-center justify-center min-h-[300px]">
          <BBALoader size={96} />
        </div>
      </div>
    );
  }

  return (
    <div className={`p-6 ${textMain}`}>
      <PortalBackground />
      <div className="relative max-w-5xl mx-auto space-y-6">
        {/* Centered hero; back + continue-tour for pending clients working ahead */}
        <header className="relative text-center">
          {isPendingStart && (
            <button
              type="button"
              onClick={() => navigate('/portal')}
              className={`absolute left-0 top-0 inline-flex items-center gap-1 text-sm font-medium ${darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
          )}
          {isPendingStart && (
            <button
              type="button"
              onClick={() => navigate('/portal/profile', { state: { resumeTourAt: 1 } })}
              className="absolute right-0 top-0 inline-flex items-center gap-1 px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700"
            >
              Continue Kickoff <ChevronRight className="w-4 h-4" />
            </button>
          )}
          <div className="mx-auto w-16 h-16 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mb-3">
            <span className="text-4xl leading-none" aria-hidden="true">🎯</span>
          </div>
          <h1 className="text-3xl font-bold text-center">Top Priorities</h1>
          <p className={`mt-2 text-center max-w-xl mx-auto ${textMuted}`}>
            {isPendingStart
              ? 'Tell us what matters most in your company — tap the cards to rank each area, then drag your top picks into order. Everything saves as you go.'
              : 'The priorities you set for your company — this is the map your BBA team works from.'}
          </p>
        </header>

        {companyId && account && (
          <PortalKickoffPriorities
            accountId={companyId}
            account={account}
            darkMode={darkMode}
            readOnly={!isPendingStart}
          />
        )}
      </div>
    </div>
  );
};

export default PortalKickoff;
